import { isPlatformBrowser, NgClass, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { PrimeNGModule } from '../../../primeng.module';
import { MercadoPagoService } from '../../../services/mercado-pago/mercado-pago.service';
import InstallmentResponse from '../../models/mercado-pago/installment-response';
import PayerCost from '../../models/mercado-pago/payer-cost';
import Card from '../../models/payment/card';
import User from '../../models/user/user';
import { calculateTotalWithInterest } from '../../utils/installment.util';

export class CardInputError {
  public errorCardNumber = false;
  public errorCvv = false;
  public errorDocument = false;
  public errorExpirationDate = false;
  public errorName = false;
  public errorInstallments = false;
}

@Component({
  selector: 'app-credit-card-input',
  standalone: true,
  imports: [NgIf, NgClass, PrimeNGModule],
  templateUrl: './credit-card-input.component.html',
  styleUrl: './credit-card-input.component.scss',
})
export class CreditCardInputComponent implements AfterViewInit, OnDestroy {
  public static CREDIT_CARD = 'CREDIT_CARD';
  public static MEDIUM = 'MEDIUM';
  public static BRAND = 'brand';
  public static INSTALLMENT = 'installments';

  @Input()
  public card?: Card;

  @Input()
  public user?: User;

  @Input()
  public installments?: InstallmentResponse;

  @Input()
  public cardInputError?: CardInputError;

  @Input()
  public disabled = false;

  @Output()
  public onChangeInstallments: EventEmitter<PayerCost> = new EventEmitter();

  private observer: MutationObserver | null = null;

  constructor(
    private mercadoPagoService: MercadoPagoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const selectElement = document.getElementById(
      'form-checkout__installments'
    );
    if (selectElement) {
      selectElement.addEventListener('change', (event) => {
        const selectedValue = Number((event.target as HTMLSelectElement).value);
        this.updateCardField('installments', selectedValue);

        const payerCost = this.installments?.payer_costs?.find(
          (p) => p.installments === selectedValue
        );

        if (payerCost) {
          this.onChangeInstallments.emit(payerCost);
        }
      });
    }

    const selectEl = document.getElementById(
      'form-checkout__identificationType'
    );
    if (selectEl) {
      selectEl.addEventListener('change', (event: any) => {
        if (this.card) {
          this.card.identificationType = event.target.value;
        }
      });
    }
  }

  onComplete() {
    this.validCardNumber();
  }

  onKeydown() {
    this.validCardNumber();
  }

  validCardNumber() {
    if (this.card?.cardNumber) {
      const value: string = this.card?.cardNumber?.replace(/\D/g, '') || '';
      if (value.length === 16) {
        this.getInstallments();
      }
    }
  }

  async getInstallments() {
    this.installments = new InstallmentResponse();
    try {
      const installmentsMP: InstallmentResponse =
        await this.mercadoPagoService.getInstallments({
          cardNumber: this.card?.cardNumber?.replace(/\D/g, '') || '',
          transactionAmount: this.card?.transactionAmount || 0,
        });

      if (installmentsMP && installmentsMP.payer_costs) {
        for (const cost of installmentsMP.payer_costs) {
          const months = parseInt(cost?.installments?.toString() || '', 10);

          const { totalWithInterest, interestLabel } =
            calculateTotalWithInterest(
              this.card?.transactionAmount || 0,
              months
            );
          cost.total_amount = totalWithInterest;

          const installmentValue = +(totalWithInterest / months).toFixed(2);
          cost.installment_amount = installmentValue;

          const textContent = `${months}x de R$ ${installmentValue.toFixed(
            2
          )} (${interestLabel})`;
          cost.recommended_message = textContent;
        }

        this.installments = installmentsMP;

        if (this.card) {
          this.card.issuerId = this.installments.issuer?.id;
          this.card.paymentMethodId = this.installments.payment_method_id;
        }

        this.populateInstallmentsCombo();
      }
    } catch (err) {
      console.error(err);
    }
  }

  populateInstallmentsCombo(): void {
    const selectElement = document.getElementById(
      'form-checkout__installments'
    ) as HTMLSelectElement;

    if (!selectElement || !this.installments?.payer_costs) return;

    selectElement.innerHTML =
      '<option disabled selected>Número de parcelas</option>';

    for (const cost of this.installments.payer_costs) {
      const option = document.createElement('option');
      option.value = cost?.installments?.toString() || '';
      option.textContent = cost?.recommended_message || '';
      selectElement.appendChild(option);
    }
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  updateCardField<K extends keyof Card>(field: K, value: Card[K]) {
    if (this.card) {
      this.card[field] = value;
    }
  }

  updateIdentificationType(event: any) {
    if (event) {
    }
  }
}
